import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { SOCKET_EVENTS } from '@forever/shared';
import { SESSION_EXPIRED_EVENT, SOCKET_URL, ensureFreshSession, getTokens } from '@/lib/api';

/**
 * Panelin canlı bağlantısı. Sunucu Socket.IO'yu `/realtime` yolunda yayınlar
 * (apps/api/src/realtime/index.ts); el sıkışmada erişim jetonu beklenir.
 *
 * Tek bir bağlantı paylaşılır: her ekran kendi soketini açarsa sunucu tarafında
 * aynı kullanıcı için gereksiz oda kaydı oluşur ve "çevrimiçi" durumu kirlenir.
 */
const SOCKET_PATH = '/realtime';

let socket: Socket | null = null;

function create(): Socket {
  const instance = io(SOCKET_URL, {
    path: SOCKET_PATH,
    autoConnect: false,
    transports: ['websocket', 'polling'],
    /*
     * Jeton yenilenebildiği için değeri sabitlemiyoruz: her el sıkışmada
     * (ilk bağlantı ve her yeniden bağlanma) güncel jeton okunur.
     */
    auth: (cb: (data: Record<string, unknown>) => void) => {
      cb({ token: getTokens()?.accessToken ?? '' });
    },
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });

  // Oturum düştüğünde bağlantıyı da kapatıyoruz; geçersiz jetonla döngüye girmesin.
  let sessionExpired = false;
  window.addEventListener(SESSION_EXPIRED_EVENT, () => {
    sessionExpired = true;
    instance.disconnect();
  });

  /*
   * Sunucu, erişim jetonu bittiğinde ve jeton her yenilendiğinde o oturumun
   * soketini kendisi kapatır ("io server disconnect"). Socket.IO bu durumda
   * kendiliğinden yeniden bağlanmaz; kapatmazsak gelen arama ve yeni mesaj
   * bildirimleri sessizce kesilir. Jetonu tazeleyip yeniden bağlanıyoruz.
   */
  let reconnecting = false;
  const reconnect = () => {
    if (reconnecting || sessionExpired || !getTokens()) return;
    reconnecting = true;
    void ensureFreshSession()
      .then((ok) => {
        if (ok && !instance.connected) instance.connect();
      })
      .finally(() => {
        reconnecting = false;
      });
  };
  instance.on('disconnect', (reason) => {
    if (reason === 'io server disconnect') setTimeout(reconnect, 500);
  });
  let failures = 0;
  instance.on('connect', () => {
    sessionExpired = false;
    failures = 0;
  });
  // El sıkışma reddedildiyse (ör. süresi dolmuş jeton) aynı yol; art arda
  // reddedilirse bekleme 1 sn'den 30 sn'ye kadar katlanarak uzar.
  instance.on('connect_error', () => {
    failures += 1;
    setTimeout(reconnect, Math.min(30_000, 1000 * 2 ** (failures - 1)));
  });

  return instance;
}

export function getSocket(): Socket {
  socket ??= create();
  return socket;
}

/** Bağlantıyı açar (zaten açıksa dokunmaz) ve paylaşılan soketi döndürür. */
export function connectSocket(): Socket {
  const instance = getSocket();
  if (!instance.connected) instance.connect();
  return instance;
}

export function closeSocket(): void {
  socket?.disconnect();
}

/**
 * Bir soket olayını dinler. İşleyici her renderda değişse de dinleyici yeniden
 * kaydedilmez; çağrı anında güncel hâli kullanılır.
 */
export function useSocketEvent<T = unknown>(event: string, handler: (payload: T) => void): void {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const instance = connectSocket();
    const listener = (...args: unknown[]) => handlerRef.current(args[0] as T);
    instance.on(event, listener);
    return () => {
      instance.off(event, listener);
    };
  }, [event]);
}

/**
 * Konuşma odasına katılır. Sunucu yetkiyi kendisi doğrular; erişimi olmayan
 * kullanıcı odaya alınmaz. Ekran kapanınca odadan çıkılır.
 */
export function useConversationRoom(conversationId: string | undefined): void {
  useEffect(() => {
    if (!conversationId) return;

    const instance = connectSocket();
    const join = () => instance.emit(SOCKET_EVENTS.CONVERSATION_JOIN, conversationId);

    join();
    // Yeniden bağlanmada oda üyeliği sıfırlanır; tekrar katılıyoruz.
    instance.on('connect', join);

    return () => {
      instance.off('connect', join);
      if (instance.connected) {
        instance.emit(SOCKET_EVENTS.CONVERSATION_LEAVE, conversationId);
      }
    };
  }, [conversationId]);
}

/** Canlı bağlantı kurulu mu — arayüzde küçük bir gösterge için. */
export function useSocketConnected(): boolean {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const instance = connectSocket();
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    setConnected(instance.connected);
    instance.on('connect', onConnect);
    instance.on('disconnect', onDisconnect);

    return () => {
      instance.off('connect', onConnect);
      instance.off('disconnect', onDisconnect);
    };
  }, []);

  return connected;
}
